import type { OnInit } from '@angular/core';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { IslandService, type Island } from '@echospaces-web-monorepo/es-portal-islands';
import {
  ButtonComponent, CardComponent, ConfirmDialogComponent, ContentTypeBadgeComponent,
  DropdownMenuComponent, EmptyStateComponent, FieldComponent, FilterChipsComponent,
  InputComponent, ModalComponent, PaginationComponent, SearchBarComponent,
  SelectComponent, SpinnerComponent, ToastService, TypeaheadComponent,
  type MenuItem, type SelectOption,
} from '@echospaces-web-monorepo/es-shared-ui';

import { ContentService, type Content, type ContentType } from './content.service';

const TYPE_OPTIONS: SelectOption[] = [
  { value: 'image', label: 'Image' }, { value: 'video', label: 'Video' },
  { value: 'video-stream', label: 'Video Stream' }, { value: 'audio', label: 'Audio' },
  { value: 'audio-stream', label: 'Audio Stream' }, { value: 'model', label: '3D Model' },
  { value: 'text', label: 'Text' }, { value: 'rich-text', label: 'Rich Text' },
  { value: 'list', label: 'List' },
];

const FILTER_OPTIONS = ['All', 'Image', 'Video', 'Audio', '3D Model', 'Text'];

/**
 * Content library overview, scoped to a picked island root. Lists every
 * content attached directly to the island; for content on descendant
 * spaces, drill into the spaces tree (Placeholders tab per space —
 * separate work). Click on a row to open the detail page.
 *
 * Create uploads to the currently-picked island.
 */
@Component({
  selector: 'es-content-list-page',
  imports: [
    FormsModule,
    ButtonComponent, SearchBarComponent, CardComponent, SpinnerComponent,
    EmptyStateComponent, ContentTypeBadgeComponent, FilterChipsComponent,
    ModalComponent, FieldComponent, InputComponent, SelectComponent,
    PaginationComponent, DropdownMenuComponent, ConfirmDialogComponent,
    TypeaheadComponent,
  ],
  template: `
    <div class="max-w-6xl mx-auto">
      <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <div>
          <h1 class="text-2xl font-bold font-exo">Content</h1>
          <p class="text-sm text-[var(--text-secondary)]">
            @if (selectedIsland(); as island) {
              {{ filteredContents().length }} items attached to {{ island.name }}
            } @else {
              Pick an island to browse its content library
            }
          </p>
        </div>
        <es-button
          variant="primary"
          [disabled]="!selectedIsland()"
          (clicked)="showCreateModal.set(true)"
        >+ New Content</es-button>
      </div>

      <!-- Island picker -->
      <div class="mb-4 max-w-md">
        <es-typeahead
          [items]="islandService.islands()"
          [loading]="islandService.loading()"
          [value]="selectedIsland()"
          [trackByFn]="trackIslandById"
          [labelFn]="islandLabel"
          [sublabelFn]="islandSlug"
          placeholder="Pick an island…"
          noItemsText="No islands available"
          (itemSelected)="onIslandPicked($event)"
        ></es-typeahead>
      </div>

      @if (!selectedIsland()) {
        <es-empty-state
          title="Pick an island"
          description="Content is space-scoped. Choose an island above to see its library."
          icon="🏝"
        ></es-empty-state>
      } @else {
        <!-- Filters -->
        <div class="flex items-center gap-4 mb-4">
          <es-search-bar class="flex-1" placeholder="Search content..." [(value)]="searchQuery"></es-search-bar>
          <es-filter-chips
            [options]="filterOptions"
            [selected]="filterType()"
            (selectionChange)="filterType.set($event)"
          ></es-filter-chips>
        </div>

        @if (contentService.loading()) {
          <div class="flex justify-center py-12">
            <es-spinner label="Loading content..."></es-spinner>
          </div>
        } @else if (filteredContents().length === 0) {
          <es-empty-state
            title="No content"
            description="Upload your first content to get started."
            icon="📦"
            actionLabel="Create Content"
            (action)="showCreateModal.set(true)"
          ></es-empty-state>
        } @else {
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            @for (item of paginatedContents(); track item.id) {
              <es-card [clickable]="true" (clicked)="openDetail(item)">
                <div class="flex items-start justify-between">
                  <div class="flex-1 min-w-0">
                    <h3 class="font-medium truncate">{{ item.title }}</h3>
                    @if (item.description) {
                      <p class="text-xs text-[var(--text-secondary)] mt-1 line-clamp-2">{{ item.description }}</p>
                    }
                    <div class="mt-2">
                      <es-content-type-badge [type]="item.type"></es-content-type-badge>
                    </div>
                  </div>
                  <es-dropdown-menu [items]="contentActions" (itemSelected)="onContentAction($event, item)">
                    <span trigger class="text-[var(--text-secondary)] hover:text-[var(--text)] cursor-pointer">⋯</span>
                  </es-dropdown-menu>
                </div>
              </es-card>
            }
          </div>

          <es-pagination
            [total]="filteredContents().length"
            [page]="page()"
            [pageSize]="pageSize"
            (pageChange)="page.set($event)"
          ></es-pagination>
        }
      }

      <!-- Create Modal -->
      <es-modal [open]="showCreateModal()" title="New Content" (closed)="showCreateModal.set(false)">
        <div class="p-6 flex flex-col gap-4">
          <es-field label="Title" [required]="true">
            <es-input
              placeholder="Content title"
              [ngModel]="newTitle()"
              (ngModelChange)="newTitle.set($event)"
            ></es-input>
          </es-field>
          <es-field label="Type" [required]="true">
            <es-select
              [options]="typeOptions"
              placeholder="Select type"
              [ngModel]="newType()"
              (ngModelChange)="newType.set($event)"
            ></es-select>
          </es-field>
          <es-field label="Description">
            <es-input
              placeholder="Optional description"
              [ngModel]="newDescription()"
              (ngModelChange)="newDescription.set($event)"
            ></es-input>
          </es-field>
          <es-field label="File" [required]="true" hint="Initial upload becomes the default-language ('all') variant.">
            <input
              type="file"
              class="w-full text-sm text-[var(--text-secondary)] file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-echo-blue/20 file:text-echo-blue hover:file:bg-echo-blue/30 file:cursor-pointer"
              (change)="onCreateFile($event)"
            />
          </es-field>
        </div>
        <div class="flex justify-end gap-2 px-6 py-4 border-t border-[var(--border)]">
          <es-button variant="secondary" (clicked)="showCreateModal.set(false)">Cancel</es-button>
          <es-button
            variant="primary"
            [disabled]="creating() || !canCreate()"
            (clicked)="create()"
          >{{ creating() ? 'Creating…' : 'Create' }}</es-button>
        </div>
      </es-modal>

      <!-- Delete Confirm -->
      <es-confirm-dialog
        [open]="showDeleteConfirm()"
        title="Delete Content"
        message="Are you sure you want to delete this content? This action cannot be undone."
        confirmLabel="Delete"
        [destructive]="true"
        (confirmed)="confirmDelete()"
        (cancelled)="showDeleteConfirm.set(false)"
      ></es-confirm-dialog>
    </div>
  `,
})
export class ContentListPageComponent implements OnInit {
  readonly contentService = inject(ContentService);
  readonly islandService = inject(IslandService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);

  searchQuery = signal('');
  filterType = signal('All');
  filterOptions = FILTER_OPTIONS;
  typeOptions = TYPE_OPTIONS;
  page = signal(1);
  pageSize = 12;

  selectedIsland = signal<Island | null>(null);

  showCreateModal = signal(false);
  showDeleteConfirm = signal(false);
  deleteTargetId = signal<string | null>(null);

  readonly newTitle = signal('');
  readonly newType = signal('');
  readonly newDescription = signal('');
  readonly newFile = signal<File | null>(null);
  readonly creating = signal(false);

  contentActions: MenuItem[] = [
    { id: 'open', label: 'Open', icon: '↗' },
    { id: 'delete', label: 'Delete', icon: '🗑', danger: true },
  ];

  readonly trackIslandById = (i: Island) => i.id;
  readonly islandLabel = (i: Island) => i.name;
  readonly islandSlug = (i: Island) => i.slug;

  readonly canCreate = computed(() =>
    !!this.selectedIsland() && !!this.newTitle().trim() && !!this.newType() && !!this.newFile());

  filteredContents = computed(() => {
    let items = this.contentService.contents();
    const q = this.searchQuery().toLowerCase();
    if (q) items = items.filter((c) => c.title.toLowerCase().includes(q));
    const f = this.filterType();
    if (f !== 'All') {
      const typeMap: Record<string, string[]> = {
        Image: ['image'], Video: ['video', 'video-stream'],
        Audio: ['audio', 'audio-stream'], '3D Model': ['model'],
        Text: ['text', 'rich-text', 'list'],
      };
      const types = typeMap[f] ?? [];
      items = items.filter((c) => types.includes(c.type));
    }
    return items;
  });

  paginatedContents = computed(() => {
    const start = (this.page() - 1) * this.pageSize;
    return this.filteredContents().slice(start, start + this.pageSize);
  });

  ngOnInit(): void {
    if (this.islandService.islands().length === 0) {
      this.islandService.loadIslands();
    }
  }

  onIslandPicked(island: Island): void {
    this.selectedIsland.set(island);
    this.page.set(1);
    this.contentService.loadContents(island.id);
  }

  onCreateFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.newFile.set(input.files?.[0] ?? null);
  }

  async create(): Promise<void> {
    const island = this.selectedIsland();
    const file = this.newFile();
    if (!island || !file || !this.newTitle() || !this.newType()) return;
    this.creating.set(true);
    try {
      await this.contentService.createContent(
        {
          title: this.newTitle().trim(),
          type: this.newType() as ContentType,
          description: this.newDescription().trim() || undefined,
          spaceId: island.id,
        },
        file,
      );
      this.toast.success('Content created');
      this.showCreateModal.set(false);
      this.newTitle.set('');
      this.newType.set('');
      this.newDescription.set('');
      this.newFile.set(null);
    } catch {
      this.toast.error('Failed to create content');
    } finally {
      this.creating.set(false);
    }
  }

  openDetail(content: Content): void {
    this.router.navigate(['/content', content.id]);
  }

  onContentAction(item: MenuItem, content: Content): void {
    if (item.id === 'open') this.openDetail(content);
    if (item.id === 'delete') {
      this.deleteTargetId.set(content.id);
      this.showDeleteConfirm.set(true);
    }
  }

  async confirmDelete(): Promise<void> {
    const id = this.deleteTargetId();
    if (id) {
      try {
        await this.contentService.deleteContent(id);
        this.toast.success('Content deleted');
      } catch {
        this.toast.error('Failed to delete');
      }
    }
    this.showDeleteConfirm.set(false);
    this.deleteTargetId.set(null);
  }
}
